/**
 * Settings. Super admin only, and the rail does not show it to anyone else.
 *
 * Tabs for machines (tolerance and material lists), logins and the machines
 * each runs, loom operators, the negative-stock policy, and demo data: fill
 * the plant with a fortnight that behaves like the real one, and take every
 * row of it back out again.
 *
 * The removal button is the reason this screen is careful. A one-click purge
 * that is nearly complete is worse than no button at all — it would leave
 * invented figures in a ledger somebody is about to trust, with nothing left
 * to identify them by. So the screen says exactly what will go, asks once, and
 * reports what actually happened rather than a cheerful noise.
 */

import { useCallback, useEffect, useState } from "react";
import { ApiError } from "../../lib/api";
import { ops } from "../../lib/api2";
import type { DemoStatus, User } from "../../lib/types";
import { fmtDate, fmtQty } from "../../lib/format";
import { FactoryManualPanel } from "./FactoryManual";
import {
  MachinesPanel,
  OperatorsPanel,
  StockPolicyPanel,
  UsersPanel,
} from "./EntryControls";

type Tab = "manual" | "machines" | "users" | "operators" | "stock" | "demo";

const TABS: [Tab, string][] = [
  ["manual", "Factory manual"],
  ["machines", "Machines & materials"],
  ["users", "Logins"],
  ["operators", "Loom operators"],
  ["stock", "Negative stock"],
  ["demo", "Demo data"],
];

const DAY_CHOICES = [7, 14, 30];

/** Table names as a person would say them, for the load and purge summaries. */
const LABELS: Record<string, string> = {
  movement: "ledger movements",
  production_run: "production runs",
  loom_shift: "loom shift rows",
  ink_entry: "ink entries",
  contractor_output: "contractor output rows",
  contractor_rate: "contractor rates",
  cutting_line: "cutting lines",
  stitching_line: "stitching lines",
  dispatch: "dispatches",
  dispatch_line: "dispatch lines",
  purchase: "goods receipts",
  rate_card: "rate cards",
  recipe: "recipe rows",
  runs_verified: "runs verified",
  voucher: "vouchers",
  wip_position: "WIP positions",
};

function label(key: string): string {
  return LABELS[key] ?? key.replace(/_/g, " ");
}

function Summary({ counts }: { counts: Record<string, number> }) {
  const rows = Object.entries(counts)
    .filter(([, n]) => n > 0)
    .sort(([, a], [, b]) => b - a);
  if (!rows.length) return null;
  return (
    <ul className="settings__counts">
      {rows.map(([key, n]) => (
        <li key={key}>
          <span className="num mono">{fmtQty(n, 0)}</span> {label(key)}
        </li>
      ))}
    </ul>
  );
}

export function Settings({ user }: { user: User }) {
  const [tab, setTab] = useState<Tab>("manual");

  // The rail already hides this screen, but a rail is a convenience and this
  // is the actual rule. The server enforces it a third time.
  if (user.role !== "SUPER_ADMIN") {
    return (
      <div className="page">
        <div className="state state--empty">
          <span className="state__glyph" aria-hidden="true">
            ⚙
          </span>
          <p>Settings are open to a super admin only.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <header className="page__head">
        <div>
          <h2 className="page__title">Settings</h2>
          <p className="page__sub">
            Administration for this installation — super admin only
          </p>
        </div>
      </header>
      <div className="tabs" role="tablist">
        {TABS.map(([key, label]) => (
          <button key={key} role="tab" aria-selected={tab === key}
                  onClick={() => setTab(key)}>
            {label}
          </button>
        ))}
      </div>
      {tab === "manual" && <FactoryManualPanel />}
      {tab === "machines" && <MachinesPanel />}
      {tab === "users" && <UsersPanel selfId={user.id} />}
      {tab === "operators" && <OperatorsPanel />}
      {tab === "stock" && <StockPolicyPanel />}
      {tab === "demo" && <DemoData />}
    </div>
  );
}

function DemoData() {
  const [state, setState] = useState<DemoStatus | null>(null);
  // Distinguishes "still loading" from "asked, and it did not work". Without
  // it a failed status call left the panel shimmering a skeleton for ever,
  // which reads as a slow server rather than as the thing that actually
  // happened — most often an API process still running the code from before
  // this screen existed.
  const [failed, setFailed] = useState<string | null>(null);
  const [days, setDays] = useState(14);
  const [busy, setBusy] = useState<"load" | "purge" | null>(null);
  const [confirmingPurge, setConfirmingPurge] = useState(false);
  // Set when the server refuses a load because real entries are already here.
  // The second attempt carries `force`, and this is what the person is
  // agreeing to when they press again.
  const [overwriteWarning, setOverwriteWarning] = useState<string | null>(null);
  const [banner, setBanner] = useState<{
    tone: "positive" | "negative" | "warning";
    text: string;
    detail?: Record<string, number>;
    notes?: string[];
  } | null>(null);

  const load = useCallback(() => {
    setFailed(null);
    ops
      .demoStatus()
      .then((s) => {
        setState(s);
        setFailed(null);
      })
      .catch((e: ApiError) =>
        setFailed(
          // A 404 here has one cause worth naming: the API process is older
          // than this screen. Saying "Not Found" and leaving them to work
          // that out is the sort of message that costs an afternoon.
          e.status === 404
            ? "This API does not have the settings endpoints. The server is " +
              "running code from before this screen existed — restart it " +
              "(stop.bat, then start.bat) and refresh."
            : e.message
        )
      );
  }, []);

  useEffect(load, [load]);

  async function loadDemo(force: boolean) {
    setBusy("load");
    setBanner(null);
    try {
      const result = await ops.loadDemoData(days, force);
      setState(result.status);
      setOverwriteWarning(null);
      setBanner({
        tone: "positive",
        text:
          `${fmtQty(result.rows, 0)} rows of demo data are in, covering ` +
          `${fmtDate(result.from_date)} to ${fmtDate(result.to_date)}. ` +
          `Every screen has something to show.`,
        detail: result.created,
        notes: result.warnings,
      });
    } catch (err) {
      const e = err as ApiError;
      if (e.status === 409) {
        // Not a failure — a question. Pressing again answers it.
        setOverwriteWarning(e.message);
        setBanner(null);
      } else {
        setOverwriteWarning(null);
        setBanner({ tone: "negative", text: e.message });
      }
    } finally {
      setBusy(null);
    }
  }

  async function purgeDemo() {
    setBusy("purge");
    setBanner(null);
    try {
      const result = await ops.removeDemoData();
      setState(result.status);
      setConfirmingPurge(false);
      setBanner({
        tone: "positive",
        text: result.rows_deleted
          ? `${fmtQty(result.rows_deleted, 0)} demo rows removed. ` +
            `${
              result.looms_restored
                ? `${fmtQty(result.looms_restored, 0)} looms were put back the way they were. `
                : ""
            }Nothing else in the database was touched.`
          : "There was no demo data to remove.",
        detail: result.by_table,
      });
    } catch (err) {
      const e = err as ApiError;
      setBanner({ tone: "negative", text: e.message });
    } finally {
      setBusy(null);
    }
  }

  return (
    <div>

      {banner && (
        <div className={`banner banner--${banner.tone}`} role="status">
          <div>
            {banner.text}
            {banner.detail && <Summary counts={banner.detail} />}
            {banner.notes && banner.notes.length > 0 && (
              <ul className="banner__list">
                {banner.notes.map((note) => (
                  <li key={note}>{note}</li>
                ))}
              </ul>
            )}
          </div>
          <button
            className="btn btn--ghost btn--auto"
            onClick={() => setBanner(null)}
          >
            Dismiss
          </button>
        </div>
      )}

      <section className="card">
        <h3 className="section-title">Demo data</h3>
        <p className="section-note">
          A working fortnight of plant activity: scrap bought in, the recycling
          line and tape plant run shift by shift, the loom floor woven, fabric
          printed, cut and stitched, and trucks loaded out. It is written
          through the same code a person's keystrokes reach, so the material
          balance closes, negative stock is flagged and the variances are real
          arithmetic. Some shifts run outside tolerance and some consignments
          weigh in short — on purpose, because a demo where nothing is ever
          wrong teaches the wrong thing.
        </p>

        {failed ? (
          <div className="state state--error">
            <span className="state__glyph" aria-hidden="true">
              ⚠
            </span>
            <p>{failed}</p>
            <button className="btn btn--ghost btn--auto" onClick={load}>
              Try again
            </button>
          </div>
        ) : !state ? (
          <div className="state">
            {[0, 1].map((i) => (
              <div key={i} className="skeleton state__bar" />
            ))}
          </div>
        ) : (
          <>
            <dl className="settings__facts">
              <div>
                <dt>Status</dt>
                <dd>
                  {state.loaded ? (
                    <span className="pill pill--warning">
                      ◷ {fmtQty(state.row_count, 0)} demo rows present
                    </span>
                  ) : (
                    <span className="pill">None loaded</span>
                  )}
                </dd>
              </div>
              {state.loaded && (
                <>
                  <div>
                    <dt>Covering</dt>
                    <dd className="num mono">
                      {state.first_date ? fmtDate(state.first_date) : "—"} –{" "}
                      {state.last_date ? fmtDate(state.last_date) : "—"}
                    </dd>
                  </div>
                  <div>
                    <dt>Loaded by</dt>
                    <dd>
                      {state.loaded_by ?? "—"}
                      {state.loaded_at ? `, ${fmtDate(state.loaded_at)}` : ""}
                    </dd>
                  </div>
                </>
              )}
              <div>
                <dt>Real ledger movements</dt>
                <dd className="num mono">
                  {fmtQty(state.real_movements, 0)}
                </dd>
              </div>
            </dl>

            {!state.loaded ? (
              <>
                <div className="page__footer">
                  <label className="inputgroup">
                    <span className="label">Days of activity</span>
                    <select
                      value={days}
                      onChange={(e) => {
                        setDays(Number(e.target.value));
                        setOverwriteWarning(null);
                      }}
                    >
                      {DAY_CHOICES.map((n) => (
                        <option key={n} value={n}>
                          {n} days
                        </option>
                      ))}
                    </select>
                  </label>
                  <button
                    className="btn btn--auto"
                    disabled={busy !== null}
                    onClick={() => loadDemo(overwriteWarning !== null)}
                  >
                    {busy === "load"
                      ? "Building the plant…"
                      : overwriteWarning
                        ? "Load anyway"
                        : "Load demo data"}
                  </button>
                </div>

                {overwriteWarning && (
                  <div className="banner banner--warning" role="alert">
                    <div>
                      {overwriteWarning}
                      <p className="section-note">
                        Removing the demo data afterwards puts this database
                        back exactly as it is now — every real row is left
                        alone. But while both are in here, the ledger holds
                        figures that never happened.
                      </p>
                    </div>
                    <button
                      className="btn btn--ghost btn--auto"
                      onClick={() => setOverwriteWarning(null)}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </>
            ) : (
              <>
                <p className="section-note">
                  Removing takes out every row the loader created — movements,
                  runs, loom shifts, ink entries, dispatches, the rate cards it
                  added — and puts the looms back the way they were. Rows the
                  plant entered, and rates the plant set, are not touched: the
                  loader keeps a register of what it wrote, and only that
                  register authorises a deletion.
                </p>
                <div className="page__footer">
                  {confirmingPurge ? (
                    <>
                      <span className="page__note">
                        Remove {fmtQty(state.row_count, 0)} demo rows? This
                        cannot be undone.
                      </span>
                      <button
                        className="btn btn--danger btn--auto"
                        disabled={busy !== null}
                        onClick={purgeDemo}
                      >
                        {busy === "purge"
                          ? "Removing…"
                          : "Yes, remove all demo data"}
                      </button>
                      <button
                        className="btn btn--ghost btn--auto"
                        disabled={busy !== null}
                        onClick={() => setConfirmingPurge(false)}
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      className="btn btn--danger btn--auto"
                      disabled={busy !== null}
                      onClick={() => setConfirmingPurge(true)}
                    >
                      Remove all demo data
                    </button>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </section>
    </div>
  );
}
